import type { RoundEvent } from "@slot/client-engine";
import type { BaseGridPresenter } from "@slot/mechanics-core";
import type { GridCodec, GridModel, Log, PresentationBarrier } from "@slot/presentation";
import type { HoldCellsTarget } from "./HoldCellsActionHandler";
import type { HoldLandingProfile } from "./HoldMotionTuning";
import type { HoldSpinMechanic } from "./HoldSpinMechanic";

/**
 * The hold mechanic's adapter to the engine's event frames (decision 50): a frame is decoded on `prepareEventFrame`
 * (after the "spin" barrier) and landed on `presentEventFrame` — every cell the frame does not hold rolls and lands on
 * it, in the order the title gives the event (its anticipated columns or cells rolling longer). The held set is the
 * one `hold_cells` stated for this frame (it runs in the frame's `frame_prepare` phase, just before the reveal); a
 * frame without one holds nothing. `paintGrid` renders a grid without motion (boot, recovery).
 */
export class HoldGridPresenter implements BaseGridPresenter, HoldCellsTarget {
  private readonly mechanic: HoldSpinMechanic;
  private readonly gridCodec: GridCodec;
  private readonly presentationBarrier: PresentationBarrier;
  private readonly landingProfile: (event: RoundEvent) => HoldLandingProfile;
  private readonly log: Log;
  private prepared: { key: string; model: GridModel } | null = null;
  /** The held set the next frame of its round lands with; consumed by that frame. */
  private pendingHold: { roundIdentifier: string; indices: ReadonlySet<number> } | null = null;

  public constructor(input: {
    mechanic: HoldSpinMechanic;
    gridCodec: GridCodec;
    presentationBarrier: PresentationBarrier;
    /** How the event's rolling cells land: the title's choice per event (a base spin's columns, a respin's cells). */
    landingProfile: (event: RoundEvent) => HoldLandingProfile;
    log?: Log;
  }) {
    this.mechanic = input.mechanic;
    this.gridCodec = input.gridCodec;
    this.presentationBarrier = input.presentationBarrier;
    this.landingProfile = input.landingProfile;
    this.log = input.log ?? (() => {});
  }

  public async paintGrid(encodedGridSnapshot: string): Promise<void> {
    this.mechanic.renderModel(this.gridCodec.decode(encodedGridSnapshot));
    this.prepared = null;
    this.pendingHold = null;
  }

  public holdCells(roundIdentifier: string, indices: ReadonlySet<number>): void {
    this.pendingHold = { roundIdentifier, indices };
  }

  public async prepareEventFrame(event: RoundEvent): Promise<void> {
    const encoded = event.eventContent?.encodedGridSnapshot;
    if (!encoded) return;
    await this.presentationBarrier.wait("spin");
    this.prepared = { key: this.computeEventKey(event), model: this.gridCodec.decode(encoded) };
  }

  public async presentEventFrame(event: RoundEvent): Promise<void> {
    const encoded = event.eventContent?.encodedGridSnapshot;
    if (!encoded) return;
    if (this.prepared?.key !== this.computeEventKey(event)) await this.prepareEventFrame(event);
    const prepared = this.prepared;
    this.prepared = null;
    if (!prepared) {
      this.log("HoldGridPresenter.presentEventFrame: no prepared model, skipping");
      return;
    }
    const hold = this.pendingHold;
    this.pendingHold = null;
    const held = hold && hold.roundIdentifier === event.roundIdentifier ? hold.indices : new Set<number>();
    await this.mechanic.spinToModel(prepared.model, held, this.landingProfile(event), this.parseAnticipated(event.eventContent?.anticipatedColumns), this.parseAnticipated(event.eventContent?.anticipatedCells));
  }

  /** Hold and win removes nothing: a held symbol stays until the round ends. */
  public async removeSymbolsByMask(_positionMask: string): Promise<void> {}

  /** An anticipation hint (columns, or cells — decision 54): its whole, non-negative entries, once each; none when absent. */
  private parseAnticipated(raw: unknown): number[] | undefined {
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const valid = Array.from(new Set(raw.filter((c): c is number => Number.isInteger(c) && c >= 0)));
    return valid.length > 0 ? valid : undefined;
  }

  /** The round first: an event's number is unique only within its round. */
  private computeEventKey(event: RoundEvent): string {
    return `${event.roundIdentifier}:${event.eventTypeName}:${event.eventSequenceNumber ?? ""}:${event.eventContent?.encodedGridSnapshot ?? ""}`;
  }
}
